import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/client/components/shad/collapsible";
import { ScrollArea } from "@/client/components/shad/scroll-area";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from "@/client/components/shad/sidebar";
import { Toaster } from "@/client/components/shad/sonner";
// import {
//   mainNavItems,
//   modulesNavItems,
//   preferencesNavItems,
// } from "@/client/features/navigation/navItems";
import { ModeToggle } from "@/client/features/theme/ModeToggle";
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router";
import { ChevronRight, LockIcon, MapIcon, SettingsIcon } from "lucide-react";
import type { CSSProperties } from "react";
import {
  mainNavItems,
  modulesNavItems,
  preferencesNavItems,
} from "./-sidenav-links";
import { IconLock } from "@tabler/icons-react";
import { useAuth } from "@/client/hooks/useAuth";

export const Route = createFileRoute("/(protected)/settings")({
  component: RouteComponent,
});

function RouteComponent() {
  const { logout } = useAuth();

  return (
    <div className="overflow-hidden w-screen h-screen">
      <SidebarProvider
        style={
          {
            "--sidebar-width": "320px",
          } as CSSProperties
        }
      >
        <Sidebar
          collapsible="icon"
          className="overflow-hidden *:data-[sidebar=sidebar]:flex-row"
        >
          <Sidebar
            collapsible="none"
            className="w-[calc(var(--sidebar-width-icon)+1px)]! border-r"
          >
            <SidebarHeader>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    className="px-2.5 md:px-2 hover:bg-lime-500"
                  >
                    <Link
                      className="px-2.5 md:px-2 text-white duration-300  bg-lime-600  hover:bg-lime-500 hover:text-white"
                      to="/map"
                      reloadDocument
                    >
                      <MapIcon />
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
              <SidebarGroup>
                <SidebarGroupContent className="px-1.5 md:px-0">
                  <SidebarMenu>
                    {mainNavItems.map((item) => (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          asChild
                          tooltip={{
                            children: item.label,
                            hidden: false,
                          }}
                          className="px-2.5 md:px-2"
                        >
                          <Link to={item.to} reloadDocument>
                            {item.icon ? <item.icon /> : null}
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
              <div className="-my-1 border-b border-border/20" />
              <SidebarGroup>
                <SidebarGroupContent className="px-1.5 md:px-0">
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        tooltip={{
                          children: "Settings",
                          hidden: false,
                        }}
                        className="px-2.5 md:px-2"
                      >
                        <SettingsIcon />
                        <span>Settings</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
            <SidebarFooter>
              <SidebarMenu>
                <SidebarMenuItem>
                  <ModeToggle />
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={logout}
                    tooltip={{
                      children: "Lock",
                      hidden: false,
                    }}
                    className="px-2.5 md:px-2"
                  >
                    <IconLock />
                    <span>Lock</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarFooter>
            <SidebarRail />
          </Sidebar>

          <Sidebar collapsible="none">
            <SidebarHeader className=" text-sm border-b px-3 py-4">
              Settings
            </SidebarHeader>
            <SidebarContent className="text-xs">
              <SidebarGroup>
                <SidebarGroupLabel>Preferences</SidebarGroupLabel>
                <SidebarMenu>
                  {preferencesNavItems.map((item) => {
                    return (
                      <SidebarMenuItem key={item.name}>
                        <SidebarMenuButton asChild>
                          <Link to={item.url}>
                            <item.icon />
                            <span>{item.name}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroup>
              <SidebarGroup>
                <SidebarGroupLabel>Modules</SidebarGroupLabel>
                <SidebarMenu>
                  {modulesNavItems.map((item) => {
                    return (
                      <Collapsible
                        key={item.title}
                        asChild
                        defaultOpen={item.isActive}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton tooltip={item.title}>
                              {item.icon && <item.icon />}
                              <span>{item.title}</span>
                              <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.items?.map((subItem) => {
                                return (
                                  <SidebarMenuSubItem key={subItem.title}>
                                    <SidebarMenuSubButton asChild>
                                      <Link to={subItem.url}>
                                        <span>{subItem.title}</span>
                                      </Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
        </Sidebar>

        <SidebarInset className="relative">
          <main className="bg-background">
            <ScrollArea className="h-screen">
              <Outlet />
            </ScrollArea>
            <Toaster />
          </main>
          {/*<ScrollArea className="h-screen">
            <div className="@container/main flex flex-1 flex-col gap-2">
              <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
                <div className="px-9 py-6 w-full mx-auto max-w-5xl">
                  <Outlet />
                  <Toaster />
                </div>
              </div>
            </div>
            <SidebarTrigger className="absolute top-2.5 left-6 bg-background size-9 shadow-lg rounded-sm hover:bg-background" />
          </ScrollArea>*/}
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
}
